import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import Button from '../components/blog/ui/Button';
import Input from '../components/blog/shared/Input';
import BlogSidebarSection from '../components/blog/BlogSidebarSection';
import BlogMiniArticleCard from '../components/blog/BlogMiniArticleCard';
import BlogBreadcrumbs from '../components/blog/BlogBreadcrumbs';
import BlogNewsletterBanner from '../components/blog/BlogNewsletterBanner';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchBlogPostBySlug, fetchBlogPosts } from '../features/blog/thunks/blog.thunk';
import fbIcon from '../assets/images/pre/FB1.svg';
import igIcon from '../assets/images/pre/IG1.svg';
import xIcon from '../assets/images/pre/x.svg';
import tgIcon from '../assets/images/pre/tgIcon.svg';
import zapIcon from '../assets/images/pre/zap1.svg';
import { getErrorMessage } from '../utils/helpers/error.helper';
import { BlogPost } from '../features/blog/interface/blog.interface';

const stripHtml = (html: string) => {
  return html.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
};

const BlogArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const dispatch = useAppDispatch();
  const { currentPost: article, posts, isSinglePostLoading: isLoading, error } = useAppSelector((state) => state.blog);
  
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  useEffect(() => {
    if (slug) {
      dispatch(fetchBlogPostBySlug(slug));
      window.scrollTo(0, 0);
    }
  }, [slug, dispatch]);

  useEffect(() => {
    if (posts.length === 0) {
      dispatch(fetchBlogPosts(6));
    }
  }, [posts.length, dispatch]);

  const moreFromAuthor = useMemo(() => {
    if (!article) return [];
    return posts
      .filter((item: BlogPost) => item.authorId === article.authorId && item.slug !== article.slug)
      .slice(0, 3)
      .map((p: BlogPost) => ({
        ...p,
        image: p.featuredMediaUrl || '',
        tag: p.categoryNames?.[0] || 'Uncategorized',
        date: new Date(p.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase(),
        readTime: '5 min read',
        description: stripHtml(p.excerptHtml || '').slice(0, 60) + '...'
      }));
  }, [article, posts]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return posts
      .filter((item: BlogPost) => item.slug !== article.slug)
      .slice(0, 6)
      .map((p: BlogPost) => ({
        ...p,
        image: p.featuredMediaUrl || ''
      }));
  }, [article, posts]);

  const contentItems = useMemo(() => {
    if (!article?.contentHtml) return [];
    
    // Extract H2 headings for the table of contents
    const h2Regex = /<h2[^>]*>(.*?)<\/h2>/gi;
    const items: string[] = [];
    let match;
    
    while ((match = h2Regex.exec(article.contentHtml)) !== null) {
      const text = stripHtml(match[1]).trim();
      if (text) items.push(text);
    }
    
    // Fallback if no headings found
    return items.length > 0 ? items : ['Overview'];
  }, [article]);

  if (isLoading && !article) {
    return (
      <div className="w-full bg-white py-24 text-center flex flex-col items-center justify-center">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-4 border-gray-100 rounded-full border-t-primary"
        />
        <p className="mt-4 text-[13px] font-title font-medium text-gray-400">Loading article...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="w-full bg-white py-20 text-center">
        <h2 className="text-[18px] mb-3 font-title text-gray-900">{getErrorMessage(error) || 'Article not found.'}</h2>
        <Link to="/blog" className="mt-3 px-5 py-2 text-[13px] text-white bg-primary rounded-full hover:bg-primary-dark transition-colors inline-block font-description">Back to Blog</Link>
      </div>
    );
  }

  const articleUrl = `${window.location.origin}/blog/${article.slug}`;
  const socialLinks = [
    { name: 'Facebook', icon: fbIcon, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}` },
    { name: 'Twitter', icon: xIcon, href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(article.title)}` },
    { name: 'WhatsApp', icon: zapIcon, href: `https://wa.me/?text=${encodeURIComponent(`${article.title} - ${articleUrl}`)}` },
    { name: 'Instagram', icon: igIcon, href: '#' },
    { name: 'Telegram', icon: tgIcon, href: `https://t.me/share/url?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(article.title)}` },
  ];

  return (
    <div className="w-full bg-white font-description selection:bg-blue-100 selection:text-blue-900">
      {/* Reading Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-0.5 origin-left z-[100] bg-primary"
        style={{ scaleX }}
      />

      <div className="layout-container pt-6 lg:pt-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8 lg:items-start">
          
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="overflow-hidden"
          >
            <BlogBreadcrumbs
              items={[
                { label: 'Homepage', to: '/' },
                { label: 'Our blog', to: '/blog' },
                { label: article.title, active: true },
              ]}
            />

            <header className="mt-5 mb-6">
              <motion.h1 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.1] mb-4 font-title font-semibold text-[#212121] tracking-[-0.02em]" 
              >
                {article.title}
              </motion.h1>
              <div className="flex items-center gap-3 mb-5 flex-wrap">
                <div className="flex items-center gap-2">
                   <img src={article.authorAvatar || ''} alt={article.authorName} className="w-8 h-8 rounded-full border border-gray-100" />
                   <div>
                     <p className="text-[12px] font-medium font-title text-[#212121]">{article.authorName || 'Baoafrik Team'}</p>
                     <p className="text-[10px] uppercase tracking-wider text-gray-400 font-description">Contributor</p>
                   </div>
                </div>
                <div className="h-5 w-px bg-gray-200" />
                <p className="text-[12px] text-[#919191] font-description">
                  {new Date(article.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
                <div className="h-1.5 w-1.5 rounded-full bg-gray-200 mx-0.5" />
                <p className="text-[12px] text-[#919191] font-description">5 min read</p>
              </div>
              
              <div className="relative group overflow-hidden rounded-[16px]">
                <motion.img 
                  initial={{ scale: 1.1, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8 }}
                  src={article.featuredMediaUrl || ''} 
                  alt={article.title} 
                  className="w-full object-cover aspect-[16/9] max-h-[340px] hover:scale-105 transition-transform duration-700" 
                />
              </div>
            </header>

            <article 
              className="mt-6 blog-content-wrapper max-w-[720px] font-description text-[14px] leading-[1.7] text-gray-700"
            >
                <div dangerouslySetInnerHTML={{ __html: article.contentHtml }} />
            </article>
          </motion.section>

          <aside className="lg:sticky lg:top-20 self-start lg:border-l lg:pl-6 space-y-8 border-gray-100">
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <BlogSidebarSection title="Information" withIndicator>
                 <div className="mt-3 p-3.5 bg-gray-50 rounded-xl space-y-3">
                    <div className="flex justify-between items-center text-[12px]">
                       <span className="text-gray-400 font-description">Category</span>
                       <span className="font-medium font-title text-primary text-[12px]">{article.categoryNames?.[0] || 'Uncategorized'}</span>
                    </div>
                    <div className="flex justify-between items-center text-[12px]">
                       <span className="text-gray-400 font-description">Published</span>
                       <span className="font-medium font-title text-gray-700 text-[12px]">{new Date(article.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <div className="flex justify-between items-center text-[12px]">
                       <span className="text-gray-400 font-description">Reading time</span>
                       <span className="font-medium font-title text-gray-700 text-[12px]">5 min read</span>
                    </div>
                 </div>
              </BlogSidebarSection>
            </motion.div>

            {contentItems.length > 0 && (
              <BlogSidebarSection title="Table of Content" withIndicator className="hidden lg:block">
                <div className="mt-3 space-y-2 pl-1.5">
                  {contentItems.map((item: string, idx) => (
                    <motion.div 
                      key={item}
                      whileHover={{ x: 4 }}
                      className="flex items-start gap-2 cursor-pointer group"
                    >
                       <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary transition-colors" />
                       <p className="text-[12px] leading-snug text-gray-500 group-hover:text-gray-900 transition-colors font-description">{item}</p>
                    </motion.div>
                  ))}
                </div>
              </BlogSidebarSection>
            )}

            <BlogSidebarSection title="Share the blog" withIndicator>
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    value={articleUrl}
                    readOnly
                    className="flex-1 px-3 py-2 text-[10px] border-none focus:ring-0 bg-gray-100 rounded-[10px] text-gray-500 font-description"
                  />
                  <Button
                    type="button"
                    unstyled
                    onClick={() => {
                       navigator.clipboard.writeText(articleUrl);
                    }}
                    className="p-2.5 bg-gray-900 text-white rounded-lg hover:bg-black transition-all active:scale-95"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {socialLinks.map((link, index) => (
                    <motion.div
                      key={`${link.name}-${index}`}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Button
                        type="button"
                        unstyled
                        onClick={() => {
                          if (link.href !== '#') window.open(link.href, '_blank', 'width=600,height=500');
                        }}
                        className="w-8 h-8 rounded-full flex items-center justify-center bg-white shadow-sm border border-gray-100 hover:border-primary-light transition-colors"
                      >
                        <img src={link.icon} alt={link.name} className="w-4 h-4 object-contain" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </div>
            </BlogSidebarSection>

            {moreFromAuthor.length > 0 && (
              <BlogSidebarSection title={`Author's Picks`} withIndicator>
                <div className="mt-4 space-y-4">
                  {moreFromAuthor.map((post: any) => (
                    <BlogMiniArticleCard
                      key={post.id}
                      image={post.image}
                      title={post.title}
                      description={post.description}
                      tag={post.tag}
                      date={post.date}
                      readTime={post.readTime}
                      slug={post.slug}
                    />
                  ))}
                </div>
              </BlogSidebarSection>
            )}

            {relatedArticles.length > 0 && (
              <BlogSidebarSection title="Related articles" withIndicator>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {relatedArticles.map((post: BlogPost) => (
                    <Link key={post.id} to={`/blog/${post.slug}`} className="group relative overflow-hidden rounded-xl aspect-video block bg-gray-100">
                      <img 
                        src={post.featuredMediaUrl} 
                        alt={post.title} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                      />
                      <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors" />
                    </Link>
                  ))}
                </div>
              </BlogSidebarSection>
            )}
          </aside>
        </div>
      </div>

      <BlogNewsletterBanner
        title="Ready for more insights?"
        titleSize="28px"
        description="Join thousands of readers getting weekly updates on African commerce and technology."
        descriptionMaxWidthClass="max-w-[360px]"
      />
    </div>
  );
};

export default BlogArticle;
